import React from "react";

import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";

// Importazione dell'interceptor globale per axios
import setupAxiosInterceptor from "../api/axiosInterceptor";

// Token Manager per refresh automatico
import { tokenManager } from "../api/refreshTokenService/refreshTokenService";

import { routeUrls } from "../config/routeUrls";
import BaseLayout from "../layouts/baseLayout/BaseLayout";
import ProtectedLayout from "../layouts/protectedLayout/ProtectedLayout";
import PublicLayout from "../layouts/publicLayout/PublicLayout";
import AuthLayout from "../layouts/authLayout/AuthLayout";
import Page404 from "../pages/404/404";
import ApplicationFallback from "./applicationFallback/ApplicationFallback";
import ApplicationError from "./applicationError/ApplicationError";
import { ActionSidebarsProvider } from "../context/actionSidebarsContext";
import { FleetProvider } from "../context/fleetContext";
import { ServicesProvider } from "../context/servicesContext";
import { RoutesProvider } from "../context/routesContext";
import { RolesProvider } from "../context/rolesContext";
import { NotificationsProvider } from "../context/notificationsContext";
import Redirect from "./redirect/Redirect";

// Public Routes
import Authorize from "../pages/auth/authorize/Authorize";
import LoginCallback from "../pages/auth/loginCallback/LoginCallback";
import AuthorizeError from "../pages/auth/authorizeError/AuthorizeError";
import Logout from "../pages/auth/logout/Logout";

// Protected Routes
import Dashboard from "../pages/dashboard/Dashboard";
import Services from "../pages/services/Services";
import Documents from "../pages/documents/Documents";
import Contracts from "../pages/contracts/Contracts";
import FleetAndRoute from "../pages/fleetAndRoute/FleetAndRoute";
import Settings from "../pages/settings/Settings";
import Profile from "../pages/profile/Profile";

// Loaders
import { getDocumentsData } from "../loaders/documents/documentsLoader";
import { getContractsData } from "../loaders/contracts/contractsLoader";
import { getFleetData } from "../loaders/fleetLoader/FleetLoader";
import { getSettingsData } from "../loaders/settings/settingsLoader";
import { getProfileData } from "../loaders/profile/profileLoader";

// GeneralTerms
import { GeneralTermsProvider } from "../context/generalTermsContext";
import GeneralTermsVerifier from "./generalTermsVerifier/GeneralTermsVerifier";

// Account Status
import AccountStatusVerifier from "./accountStatusVerifier/AccountStatusVerifier";
import { AccountStatusProvider } from "../context/accountStatusContext";
import Trips from "../pages/trips/Trips";
import { BusinessProfileProvider } from "../context/businessProfileContext";
import { TripProvider } from "../context/tripsContext";
import TripDetailsPage from "../pages/tripDetails/TripDetails";
import { TripDocumentProvider } from "../context/TripDocumentContext";
import TripDocumentsPage from "../pages/tripDocuments/TripDocument";
import { useBusinessProfile } from "../utils/hooks/useBusinessProfile";
import ErrorPage from "../pages/errorPageTrip/ErrorPage";
//import { getTripDocumentsData } from "../loaders/tripDocument/getTripDocumentsData";

const ProtectedProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <AccountStatusProvider>
      <AccountStatusVerifier>
        <GeneralTermsProvider>
          <GeneralTermsVerifier>
            <TripDocumentProvider>
              <TripProvider>
                <BusinessProfileProvider>
                  <NotificationsProvider>
                    <ActionSidebarsProvider>
                      <ServicesProvider>
                        <FleetProvider>
                          <RolesProvider>
                            <RoutesProvider>{children}</RoutesProvider>
                          </RolesProvider>
                        </FleetProvider>
                      </ServicesProvider>
                    </ActionSidebarsProvider>
                  </NotificationsProvider>
                </BusinessProfileProvider>
              </TripProvider>
            </TripDocumentProvider>
          </GeneralTermsVerifier>
        </GeneralTermsProvider>
      </AccountStatusVerifier>
    </AccountStatusProvider>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<BaseLayout />} errorElement={<Page404 />}>
      <Route path="/" element={<Redirect to={routeUrls.dashboard} />} />

      <Route element={<AuthLayout />}>
        {/* PROTECTED ROUTES */}
        <Route
          element={
            <ProtectedProvider>
              <ProtectedLayout />
            </ProtectedProvider>
          }
          errorElement={<ApplicationError />}
        >
          <Route
            path={routeUrls.dashboard}
            element={<Dashboard />}
            errorElement={<div>Dashboard error!</div>}
          />
          <Route
            path={routeUrls.services}
            element={<Services />}
            errorElement={<div>Services error!</div>}
          />
          <Route
            path={routeUrls.documents}
            element={<Documents />}
            loader={getDocumentsData}
            errorElement={<div>Documents error!</div>}
          />
          <Route
            path={routeUrls.contracts}
            element={<Contracts />}
            loader={getContractsData}
            errorElement={<div>Contracts error!</div>}
          />
          <Route path={routeUrls.trips} element={<Trips />} />
          <Route path={routeUrls.trip} element={<TripDetailsPage />} />
          <Route
            path={routeUrls.tripDocuments}
            element={<TripDocumentsPage />}

          />
          <Route path={routeUrls.fleetAndRoute} element={<FleetAndRoute />} loader={getFleetData} />
          <Route
            path={routeUrls.settings}
            element={<Settings />}
            loader={getSettingsData}
            errorElement={<div>Settings error!</div>}
          />
          <Route
            path={routeUrls.profile}
            element={<Profile />}
            loader={getProfileData}
            errorElement={<div>Profile error!</div>}
          />
             <Route
        path={routeUrls.errorPage} 
        element={<ErrorPage/>}
        />
        </Route>
        <Route
        path={routeUrls.errorPage} 
        element={<ErrorPage/>}
        />


        {/* PUBLIC ROUTES */}
        <Route element={<PublicLayout />}>
          <Route path={routeUrls.authorize} element={<Authorize />} />
          <Route path={routeUrls.loginCallback} element={<LoginCallback />} />
          <Route path={routeUrls.authorizeError} element={<AuthorizeError />} />
          <Route path={routeUrls.logout} element={<Logout />} />
        </Route>
      </Route>
    </Route>
  )
);

const App: React.FC = () => {
  // Inizializza l'interceptor globale per axios e il token manager
  React.useEffect(() => {
    setupAxiosInterceptor();
    
    // Inizializza il token manager per refresh automatico silente
    tokenManager.initialize();
    console.log('Silent token refresh initialized');

    // Cleanup quando l'app viene dismontata
    return () => {
      tokenManager.shutdown();
    };
  }, []);

  return (
    <RouterProvider fallbackElement={<ApplicationFallback />} router={router} />
  );
};

export default App;
